import { logger } from "@lib/logger"
import type { PostHog } from "posthog-js/dist/module.slim"

import {
  ANALYTICS_APP,
  type AnalyticsEvent,
  type AnalyticsEventProperties,
  isStudentRoute,
  toRoutePattern,
} from "@/constants/analytics"
import { env } from "@/env"
import type { Professor } from "@/types"

type AnalyticsProperty = string | number | boolean | null | undefined
type AnalyticsProperties = Record<string, AnalyticsProperty>

/**
 * Imported from the SAME entrypoint the dynamic `import()` below loads, not
 * from the `posthog-js` package root. Each build ships its own structurally
 * incompatible `PostHog` class, so taking the type from the root while
 * loading the slim build at runtime does not typecheck — and, worse, would
 * describe a surface this bundle does not have. Keep the two specifiers
 * identical.
 */
type PostHogClient = PostHog

// An event with no declared properties takes no second argument at all;
// everything else requires exactly its declared shape.
type TrackArgs<E extends AnalyticsEvent> =
  AnalyticsEventProperties[E] extends Record<string, never> ? [] : [AnalyticsEventProperties[E]]

let posthogClient: PostHogClient | null = null
let loadPromise: Promise<PostHogClient | null> | null = null
let initializationFailed = false
let identifiedProfessorId: string | null = null
let identifiedSignature: string | null = null
let requestedProfessorId: string | null = null

/**
 * Super properties: PostHog attaches them to every event and persists them
 * across reloads. They are NOT re-sent per capture.
 *
 * They must, however, be re-registered after every `client.reset()`.
 * `reset()` clears persistence, super properties included, and login is an
 * in-app `navigate("/")` rather than a page load — so without that, a
 * professor who logs out and back in without reloading emits events with no
 * `environment`, the one property every filter in PostHog depends on.
 */
const commonProperties = (): AnalyticsProperties => ({
  app: ANALYTICS_APP,
  environment: env.VITE_POSTHOG_ENV,
})

/**
 * School rides on every event as a super property instead of through
 * PostHog Group Analytics, which is a paid add-on. `is_internal` is the
 * backend's `is_internal_account` (core/email_policy.py) and is what keeps
 * Platonica staff out of investor metrics. It used to read `emails_blocked`
 * — the send guard, false for any internal account with real sends on — so
 * admin@platonica.io counted as a professor.
 */
const schoolProperties = (professor: Professor): AnalyticsProperties => ({
  school_id: professor.school?.id ?? null,
  school_name: professor.school?.name ?? null,
  is_internal: professor.is_internal_account === true,
})

/**
 * PRD-57 FR-3/FR-6 — the professor's OWN identity, as person properties, so
 * support can find them by name. This is the single place professor name and
 * email leave the product; they are never event properties. Nothing about
 * any student belongs here.
 */
const personProperties = (professor: Professor): AnalyticsProperties => ({
  name: professor.full_name || professor.name,
  email: professor.email,
  ...schoolProperties(professor),
  school_city: professor.school?.city ?? null,
  school_state: professor.school?.state ?? null,
  education_level: professor.school?.education_level ?? null,
  language: professor.settings?.language ?? null,
  period_type: professor.academic_config?.period_type ?? null,
  grade_scale: professor.academic_config?.grade_scale ?? null,
  is_director: professor.settings?.is_director === true,
  onboarding_completed: professor.settings?.onboarding_completed === true,
})

/**
 * THE gate for student-facing surfaces (`/encuesta`, `/entregar/:token`).
 * Every capture path below goes through `withClient`, so none of them can
 * forget it. Read at call time, not at load time: the same tab can move
 * between a professor route and a public one.
 */
const captureAllowed = (): boolean =>
  typeof window === "undefined" || !isStudentRoute(window.location.pathname)

const currentRoute = (): string =>
  typeof window === "undefined" ? "" : toRoutePattern(window.location.pathname)

const loadPostHog = (): Promise<PostHogClient | null> => {
  if (posthogClient) return Promise.resolve(posthogClient)
  if (loadPromise) return loadPromise
  if (initializationFailed) return Promise.resolve(null)
  if (!env.VITE_POSTHOG_ENABLED || !env.VITE_POSTHOG_PROJECT_TOKEN) {
    return Promise.resolve(null)
  }

  // `dist/module.slim`, whose default extension set is EMPTY: no session
  // recorder, surveys, heatmaps, web vitals or autocapture code at all.
  // That matters beyond bytes — those products are switched ON at the
  // PostHog project level and are inert only because their classes are
  // absent here. Never import the package root, never pass `AllExtensions`.
  //
  // Exactly one class is added back: `exceptions`, without which
  // `captureException` is a silent no-op. `extension-bundles` does not
  // tree-shake (one module, no `sideEffects` flag — measured), so this costs
  // the whole file: the lazy chunk is ≈98 KB gzip instead of ≈48 KB. Paid
  // knowingly (ADR-0046 §6): it buys issue grouping, alerting and a separate
  // exception quota. Both imports share one `Promise.all` so the cost is one
  // round of parallel requests after first paint, not two in sequence.
  loadPromise = Promise.all([
    import("posthog-js/dist/module.slim"),
    import("posthog-js/dist/extension-bundles"),
  ])
    .then(([{ default: client }, { ErrorTrackingExtensions }]) => {
      client.init(env.VITE_POSTHOG_PROJECT_TOKEN, {
        api_host: env.VITE_POSTHOG_HOST,
        // When `api_host` is the reverse proxy, PostHog still needs the real
        // app origin to build links out to the UI.
        ui_host: "https://us.posthog.com",
        autocapture: false,
        capture_pageleave: false,
        capture_pageview: false,
        disable_session_recording: true,
        // Only create a person profile once `identify()` has run.
        person_profiles: "identified_only",
        // Only `exceptions` (manual capture). NOT `exceptionObserver`: that
        // one lazy-loads a script from an assets host our `/ingest` proxy
        // does not front, and is gated on a project setting. Our own two
        // window listeners (lib/observability.ts) do its job.
        __extensionClasses: { exceptions: ErrorTrackingExtensions.exceptions },
      })
      client.register(commonProperties())
      posthogClient = client
      return client
    })
    .catch((error: unknown) => {
      initializationFailed = true
      logger.warning("analytics.init_failed", { error: String(error) })
      return null
    })

  return loadPromise
}

/**
 * Every operation funnels through here, which gives three guarantees in one
 * place: the student-surface gate, call order = send order (one promise
 * chain — `reset()` included, or a synchronous reset would overtake the
 * `professor_logged_out` queued a line before it), and "analytics can never
 * break the app" (every failure is swallowed into a `logger.warning` in the
 * `analytics.` namespace, which the log sink ignores so it cannot loop).
 */
const withClient = (operation: string, run: (client: PostHogClient) => void) => {
  if (!captureAllowed()) return
  void loadPostHog().then((client) => {
    if (!client) return
    try {
      run(client)
    } catch (error: unknown) {
      logger.warning(`analytics.${operation}_failed`, { error: String(error) })
    }
  })
}

export interface ApiFailure {
  method: string
  /** Path relative to the API root, query string already removed. */
  path: string
  status: number | null
  code: string | null
  isNetwork: boolean
  isTimeout: boolean
  durationMs: number | null
  /** `navigator.onLine` when it failed: false means the device had no network. */
  isOnline: boolean
}

export const analytics = {
  /**
   * Idempotent, and safe to call on every professor-object change: it
   * identifies once per professor and afterwards only pushes person
   * properties when they actually differ (a renamed professor, a changed
   * language, an internal account whose `is_internal` was wrong before).
   */
  identifyProfessor(professor: Professor) {
    requestedProfessorId = professor.id
    const person = personProperties(professor)
    const signature = JSON.stringify(person)
    withClient("identify", (client) => {
      if (requestedProfessorId !== professor.id) return
      if (identifiedProfessorId === professor.id) {
        if (identifiedSignature === signature) return
        client.setPersonProperties(person)
      } else {
        client.identify(professor.id, person)
        identifiedProfessorId = professor.id
      }
      identifiedSignature = signature
      client.register({ ...commonProperties(), ...schoolProperties(professor) })
    })
  },

  track<E extends AnalyticsEvent>(event: E, ...args: TrackArgs<E>) {
    // `app`, `environment` and the school ride along from `register()`;
    // re-sending them here would only suggest registration had not worked.
    withClient("capture", (client) => client.capture(event, args[0] ?? {}))
  },

  /** The `logger` sink (lib/observability.ts). `context` is already allow-listed. */
  captureLog(level: "error" | "warning", logEvent: string, context: AnalyticsProperties) {
    withClient("capture_log", (client) =>
      client.capture(level === "error" ? "app_error" : "app_warning", {
        ...context,
        error_event: logEvent,
        route: currentRoute(),
      }),
    )
  },

  /** A real `Error` → PostHog Error Tracking (`$exception`). */
  captureException(error: unknown, properties: AnalyticsProperties = {}) {
    withClient("capture_exception", (client) =>
      client.captureException(error, { ...properties, route: currentRoute() }),
    )
  },

  captureApiFailure(failure: ApiFailure) {
    withClient("capture_api_failure", (client) =>
      client.capture("api_request_failed", {
        method: failure.method,
        // `/students/<uuid>` → `/students/:id`: same identifier-leak and
        // cardinality problems as page routes, same fix.
        url_pattern: toRoutePattern(failure.path),
        status: failure.status,
        code: failure.code,
        is_network: failure.isNetwork,
        is_timeout: failure.isTimeout,
        is_online: failure.isOnline,
        duration_ms: failure.durationMs,
        route: currentRoute(),
      }),
    )
  },

  reset() {
    requestedProfessorId = null
    // Nothing loaded means nothing to reset — and resetting must never be the
    // thing that lazily loads PostHog.
    if (!posthogClient) return
    withClient("reset", (client) => {
      client.reset()
      identifiedProfessorId = null
      identifiedSignature = null
      // `reset()` just wiped the super properties along with the identity.
      client.register(commonProperties())
    })
  },
}
